import { setupAutomaticEthereumWalletDerivation } from "@aptos-labs/derived-wallet-ethereum";
import { setupAutomaticSolanaWalletDerivation } from "@aptos-labs/derived-wallet-solana";
import { AptosWalletAdapterProvider } from "@aptos-labs/wallet-adapter-react";
import { CacheProvider } from "@emotion/react";
import { Theme } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";

import { ToastProvider } from "@components/ToastProvider";
import { emotionCache } from "./emotionCache";

import { RootStore } from "@stores";
import { DIALOG_TYPE } from "@stores/DialogStore";
import { StoresProvider, useStores } from "@stores/useStores";

import { TERMS_ACCEPTED_KEY, VAULT_CONTRACT_ADDRESS_KEY } from "@constants";
import { indexDB } from "@utils/indexDB";

import { DataLoaderProvider } from "./DataLoaderProvider";
import { getAptosDappConfig } from "./api/aptos";
import { ConfigProvider } from "./providers/ConfigProvider";
import { GoogleAnalyticsProvider } from "./providers/GoogleAnalyticsProvider";
import { VaultContractProvider } from "./providers/VaultContractProvider";

const STORE = RootStore.create();

const GeoRestrictionGuard: React.FC = observer(() => {
  const { geoFenceStore, accountStore, dialogStore } = useStores();
  const disconnectingRef = useRef(false);

  useEffect(() => {
    if (!geoFenceStore.isRestricted) {
      disconnectingRef.current = false;
      return;
    }

    if (dialogStore.isVisible(DIALOG_TYPE.SELECT_WALLET)) {
      dialogStore.close(DIALOG_TYPE.SELECT_WALLET);
    }
    if (dialogStore.isVisible(DIALOG_TYPE.TERMS)) {
      dialogStore.close(DIALOG_TYPE.TERMS);
    }
    if (dialogStore.isVisible(DIALOG_TYPE.START_TRADING)) {
      dialogStore.close(DIALOG_TYPE.START_TRADING);
    }

    if (!accountStore.isAuthenticated || disconnectingRef.current) {
      return;
    }

    disconnectingRef.current = true;
    void accountStore.disconnect().finally(() => {
      disconnectingRef.current = false;
    });
  }, [geoFenceStore.isRestricted, accountStore.isAuthenticated, accountStore, dialogStore]);

  return null;
});

const ConfigSync: React.FC<PropsWithChildren> = observer(({ children }) => {
  const config = STORE.configStore.config;
  const handledVaultRef = useRef<string | null>(null);

  useEffect(() => {
    if (!config) return;

    const envVault = config.perpetual_addr;
    if (handledVaultRef.current === envVault) return;
    handledVaultRef.current = envVault;

    const localVault = localStorage.getItem(VAULT_CONTRACT_ADDRESS_KEY);

    if (localVault && localVault !== envVault) {
      const termsAccepted = localStorage.getItem(TERMS_ACCEPTED_KEY);
      localStorage.clear();
      if (termsAccepted) {
        localStorage.setItem(TERMS_ACCEPTED_KEY, termsAccepted);
      }
      localStorage.setItem(VAULT_CONTRACT_ADDRESS_KEY, envVault);
      void indexDB.clear().then(() => {
        STORE.tokenStore.reset();
        STORE.subAccountsStore.reset();
        STORE.myOrdersStore.reset();
        STORE.balanceStore.reset();
        STORE.tradeboxStore.reset();
      });
    } else if (!localVault) {
      localStorage.setItem(VAULT_CONTRACT_ADDRESS_KEY, envVault);
    }
  }, [config]);

  const dappConfig = useMemo(
    () => (config ? getAptosDappConfig(config.aptos_network, config.aptos_api_key ?? "") : null),
    [config],
  );

  useEffect(() => {
    if (!dappConfig?.network) return;
    setupAutomaticEthereumWalletDerivation({ defaultNetwork: dappConfig?.network });
    setupAutomaticSolanaWalletDerivation({ defaultNetwork: dappConfig?.network });
  }, [dappConfig?.network]);

  const handleWalletError = useCallback((error: Error) => {
    if (error.name === "WalletNotConnectedError") return;
    const msg = error.message || String(error);
    const lowerMessage = msg.toLowerCase();
    if (lowerMessage.includes("rejected") || lowerMessage.includes("cancel")) {
      console.info("[WalletAdapter] User dismissed wallet action", { name: error.name, message: msg });
      return;
    }

    console.error("[WalletAdapter] Unexpected wallet error", {
      name: error.name,
      message: msg,
      error,
    });
  }, []);

  if (config && dappConfig) {
    return (
      <AptosWalletAdapterProvider dappConfig={dappConfig} autoConnect onError={handleWalletError}>
        {children}
      </AptosWalletAdapterProvider>
    );
  }

  return <>{children}</>;
});

export const Providers: React.FC<PropsWithChildren> = ({ children }) => (
  <CacheProvider value={emotionCache}>
    <GoogleAnalyticsProvider>
      <StoresProvider value={STORE}>
        <ConfigProvider>
          <ConfigSync>
            <VaultContractProvider>
              <Theme accentColor="indigo" appearance="dark">
                <ToastProvider>
                  <GeoRestrictionGuard />
                  <DataLoaderProvider />
                  {children}
                </ToastProvider>

                <div id="portal-root" />
              </Theme>
            </VaultContractProvider>
          </ConfigSync>
        </ConfigProvider>
      </StoresProvider>
    </GoogleAnalyticsProvider>
  </CacheProvider>
);
